import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Modal } from "@/shared/ui/modal";

interface ConnectReplaceDialogProps {
  /** The shop currently active on the marketplace being connected, or null
   *  when there is nothing to confirm (the dialog then renders nothing). */
  currentActiveShop: { shop_id: string; shop_name: string } | null;
  marketplaceLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

/**
 * D2: connecting a new shop on a marketplace that already has an active one
 * disconnects that shop. The operator confirms before the browser navigates
 * away to the OAuth flow.
 */
export function ConnectReplaceDialog({
  currentActiveShop,
  marketplaceLabel,
  onConfirm,
  onCancel,
  isPending,
}: ConnectReplaceDialogProps): JSX.Element | null {
  const { t } = useTranslation("marketplace_connections");
  if (!currentActiveShop) return null;
  const current = currentActiveShop.shop_name || currentActiveShop.shop_id || "";

  return (
    <Modal
      open
      onClose={onCancel}
      title={t("connect.replace_title", { marketplace: marketplaceLabel })}
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            data-testid="connect-replace-cancel"
          >
            {t("connect.cancel")}
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={isPending}
            onClick={onConfirm}
            data-testid="connect-replace-confirm"
          >
            {t("connect.confirm")}
          </Button>
        </>
      }
    >
      <div className="space-y-2 text-sm" data-testid="connect-replace-dialog" role="alert">
        <p>{t("connect.replace", { current, marketplace: marketplaceLabel })}</p>
      </div>
    </Modal>
  );
}
